import _ from "lodash";
import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useKey } from "react-use";
import classNames from "classnames";
import { compose, graphql } from "lib/apolloClient";
import ACLStore from "lib/aclStore";
import {
  setHomePageSidebar,
  getCourseFiltersMemoize,
  getUserIdAndEntityTypeByUserMemoize,
} from "Teacher/modules/TeacherModule";

import I18nHOC from "UIComponents/I18nHOC";
import { Button, IconButton, Tabs, Tooltip, Badge } from "@toddle-edu/ds-web";
import {
  MarkAsReadOutlined,
  SettingsOutlined,
  FilterThreeLinesOutlined,
  FilterThreeLinesFilled,
  CloseOutlined,
} from "@toddle-edu/ds-icons";

import {
  getSelectedAcademicYear,
  getUserEntityType,
  getUserId,
  getUserType,
  getCurrentCurriculumProgramType,
  getIsStaff,
} from "modules/CommonSelectors";
import {
  getNewNotificationCount,
  getNotificationCategoryTabs,
} from "../modules/Utils";
import {
  getIsAcademicYearEnded,
  getIsClassAnnouncementEnabled,
} from "Announcements/modules/utils";
import { getUserCoursesFromCache } from "Teacher/modules/TeacherGraphqlHelpers";
import { getUserCoursesQuery } from "Teacher/modules/TeacherQuery";
import { getSelectedCurriculumProgramIds } from "HomePage/modules/HomePageUtils";
import {
  getUserInfo,
  getUserIdOnPortalType,
} from "Login/modules/LoginModule/LoginUtils";

import {
  goToPlatformHomeChildRoute,
  goToSnpHomeChildRoute,
} from "modules/NavigationModule";
import {
  getCurriculumProgramFreeStatus,
  setThirdPaneNotificationCenterActiveTab,
} from "Platform/modules/PlatformModule";
import {
  allNotificationsRead,
  getNewValueWhenGlobalHeaderEnabled,
  isGlobalHeaderEnabled,
} from "../modules/NotificationCenterV2Modules";

import { NOTIFICATION_CATEGORY_V2 } from "Constants";
import { SNPv2_VISITOR_PORTAL_PERM_GROUP } from "Constants/permissionConstants";

import NotificationList from "./NotificationList";
import classes from "./NotificationCenterV2.scss";
import { recordEvent } from "lib/eventTracker";
import { useFirstFocus } from "Hooks";

const getInitialActiveTabValue = (userType, notificationCategory) => {
  let initialTabIndex;
  if (userType === "student") {
    initialTabIndex = 1;
  } else if (userType === "parent") {
    initialTabIndex = 2;
  } else {
    initialTabIndex = 0;
  }
  return _.get(notificationCategory[initialTabIndex], "value", "");
};

const getInitialActiveTabFilterValue = (userType, notificationCategory) => {
  let initialTabIndex;
  if (userType === "student") {
    initialTabIndex = 1;
  } else if (userType === "parent") {
    initialTabIndex = 2;
  } else {
    initialTabIndex = 0;
  }
  return _.get(notificationCategory[initialTabIndex], "filterValue", "");
};

const getTabLabelComponent = ({ label, tabCount }) => {
  const displayedTabCount = tabCount > 99 ? "99+" : tabCount;
  return (
    <Tooltip tooltip={label} placement="bottom" align={{ offset: [0, 8] }}>
      <div className={classes.tabLabelContainer}>
        <div className={classes.tabLabelStyles}>{label}</div>
        {tabCount ? `(${displayedTabCount})` : ""}
      </div>
    </Tooltip>
  );
};

const getTabLabelComponentV2 = ({
  label,
  tabCount,
  value, // current tab value to check if it is active
  thirdPaneNotificationCenterActiveTab, // active tab value
}) => {
  const displayedTabCount = tabCount > 99 ? "99+" : tabCount;
  const variant =
    thirdPaneNotificationCenterActiveTab === value ? "notifications" : "subtle";
  return (
    <Tooltip tooltip={label} placement="bottom" align={{ offset: [0, 8] }}>
      <div className={classes.tabLabelContainer}>
        <div className={classes.tabLabelStyles}>{label}</div>
        {tabCount ? (
          <Badge
            type={"numeric"}
            value={displayedTabCount}
            variant={variant}
            dsVersion="2.0"
          />
        ) : null}
      </div>
    </Tooltip>
  );
};

const AnnouncementButtons = React.memo(props => {
  const { onViewAllAnnouncementClick, t, testId } = props;
  return (
    <div className={classes.announcementButtonContainer}>
      <Button
        onClick={onViewAllAnnouncementClick}
        size={"small"}
        variant={"inline-progressive"}
        testId={`${testId}-viewAllAnnouncements`}
      >
        {t(`announcement:view_all_announcements`)}
      </Button>
    </div>
  );
});

AnnouncementButtons.displayName = "AnnouncementButtons";

export const NotificationCenterV2 = React.memo(props => {
  const {
    userType,
    courses,
    categoryTabs,
    sourceFrom,
    showNotificationCenter,
    isClassAnnouncementEnabled,
    goToPlatformHomeChildRoute,
    goToSnpHomeChildRoute,
    allNotificationsRead,
    setHomePageSidebar,
    closeNotificationCenter = null,
    toggleShowNotificationCenter = null,
    t,
    isStaff,
    newNotificationTotalCount,
    setThirdPaneNotificationCenterActiveTab,
  } = props;

  const testId = "notificationCenter-notificationCenterV2";
  const notificationCenterRef = useRef();
  const notificationListRef = useRef();

  const notificationCategory = NOTIFICATION_CATEGORY_V2;

  const [activeTab, setActiveTab] = useState({
    tabValue: getInitialActiveTabValue(userType, notificationCategory),
    tabFilterValue: getInitialActiveTabFilterValue(
      userType,
      notificationCategory
    ),
  });
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  useEffect(() => {
    const { focusableElements } = getFocusableElements();
    setThirdPaneNotificationCenterActiveTab(activeTab?.tabValue); // setting active tab in redux
    if (focusableElements) {
      setTimeout(() => {
        focusableElements[0]?.focus({ preventScroll: true });
      });
    }
  }, [activeTab]);

  const { tabValue, tabFilterValue } = activeTab;
  const isActiveTabAnnouncement = tabValue === "ANNOUNCEMENTS";
  const isFromSideBar = sourceFrom == "sidebar";
  const menuRef = useRef([]);
  // assigning -2 to activeMenuIndex to handle the case when user is navigating through tabs using (shift+tabs)
  const activeMenuIndex = useRef(-2);

  const containerClasses = classNames({
    [classes.container]: !isFromSideBar,
    [classes.sideBarContainer]: isFromSideBar,
    [classes.showContainer]: showNotificationCenter && isFromSideBar,
    [classes.hideContainer]: !showNotificationCenter && isFromSideBar,
    [classes.containerV2]: isGlobalHeaderEnabled(),
  });

  const onToggleActiveTab = tab => {
    // find notification category obj with value equal to current tab
    const notificationItem = _.find(
      notificationCategory,
      ({ value, userTypes }) => value === tab && _.includes(userTypes, userType)
    );

    //set active tab and it's filter value
    setActiveTab({
      tabValue: tab,
      tabFilterValue: _.get(notificationItem, "filterValue"),
    });

    if (notificationListRef?.current) {
      notificationListRef.current.scrollTop = 0;
    }
  };

  const handleTabKeyPress = e => {
    e.preventDefault();

    const selectedTabIndex = _.findIndex(categoryTabs, {
      value: tabValue,
    });

    const lastIndex = categoryTabs.length - 1;

    let indexToFocusNext = selectedTabIndex;

    if (e.shiftKey) {
      // if last tab is selected and user pressed shift + tab - we would move the focus of last menu item
      if (lastIndex === selectedTabIndex && activeMenuIndex.current > 0) {
        activeMenuIndex.current--;
        menuRef.current[activeMenuIndex.current]?.focus();
        return;
      } else if (selectedTabIndex === 0) {
        // if first tab is selected and user pressed shift + tab - we would move the focus of last menu item
        if (activeMenuIndex.current === -2) {
          activeMenuIndex.current = menuRef.current.length - 1;
        }

        if (activeMenuIndex.current >= 0) {
          menuRef.current[activeMenuIndex.current]?.focus();
          activeMenuIndex.current--;
          return;
        }
      }

      // if user pressed shift + tab - we would move backward
      indexToFocusNext =
        selectedTabIndex === 0 ? lastIndex : selectedTabIndex - 1;
    } else {
      // if last tab is selected and user pressed  tab - we would move the focus of menu item
      if (
        lastIndex === selectedTabIndex &&
        menuRef.current.length - 1 > activeMenuIndex.current
      ) {
        // if activeMenuIndex is -2, we would move the focus to first menu item
        if (activeMenuIndex.current === -2) {
          ++activeMenuIndex.current;
        }
        menuRef.current[++activeMenuIndex.current]?.focus();
        return;
      }

      // if user pressed only tab - we would move forward
      indexToFocusNext =
        selectedTabIndex === lastIndex ? 0 : selectedTabIndex + 1;
    }

    activeMenuIndex.current = -2;

    if (indexToFocusNext >= 0 && indexToFocusNext < categoryTabs.length) {
      onToggleActiveTab(categoryTabs?.[indexToFocusNext]?.value);
    }
  };

  const onViewAllAnnouncementClick = () => {
    goToPlatformHomeChildRoute({ route: "circulars" });
    handleCloseSidebar();
  };

  const goToNotificationSettings = () => {
    if (ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP)) {
      goToSnpHomeChildRoute({ route: "notification-preferences" });
      return;
    }

    const route = "notification-preferences";
    handleCloseSidebar();
    goToPlatformHomeChildRoute({ route });
    if (isStaff) {
      recordEvent({
        eventName:
          "[Web] - Clicked on Notification Settings from Notification panel",
      });
    }
  };

  const onMarkAllReadClicked = () => {
    allNotificationsRead({ showOnlyUnread });
    setShowOnlyUnread(false);
    if (isStaff) {
      recordEvent({
        eventName: "[Web] - Clicked Mark All as read",
      });
    }
  };

  const handleCloseSidebar = () => {
    if (closeNotificationCenter) closeNotificationCenter();
    if (toggleShowNotificationCenter) {
      // closing notification panel when opened from notification tray
      toggleShowNotificationCenter();
    } else {
      setHomePageSidebar({
        showSidebar: false,
        contentType: null,
      });
    }
  };

  const handleTickButtonClick = () => {
    if (isStaff && !showOnlyUnread) {
      recordEvent({
        eventName: "[Web] - Clicked on Show unread",
      });
    }
    setShowOnlyUnread(!showOnlyUnread);
  };

  useFirstFocus(
    getNewValueWhenGlobalHeaderEnabled(
      ".allRead",
      `.${classes.tabContainer} .border-active`
    )
  );

  const getFocusableElements = () => {
    const focusableElements = [
      ...(notificationCenterRef.current?.querySelectorAll(
        "[data-elm=notif-card]"
      ) ?? []),
    ];

    return { focusableElements };
  };

  const focusNextElement = (direction = "up") => e => {
    e.preventDefault();

    const { focusableElements } = getFocusableElements();

    const currentFocusedElement = notificationCenterRef.current?.querySelector(
      ":focus"
    );

    const currentFocusedElementIndex = _.findIndex(
      focusableElements,
      currentFocusedElement
    );

    if (currentFocusedElementIndex === -1 || currentFocusedElement === null) {
      focusableElements[0]?.focus();
      return;
    }

    let nextFocusElementIndex;
    const lastIndex = focusableElements.length - 1;

    if (direction === "up") {
      nextFocusElementIndex =
        currentFocusedElementIndex === 0 ? -1 : currentFocusedElementIndex - 1;
    } else if (direction === "down") {
      nextFocusElementIndex =
        currentFocusedElementIndex === lastIndex
          ? -1
          : currentFocusedElementIndex + 1;
    } else {
      console.error("invalid direction", direction);
      return;
    }
    if (nextFocusElementIndex >= 0) {
      focusableElements[nextFocusElementIndex]?.focus();
    }
  };

  useKey("Tab", handleTabKeyPress, {}, [tabValue]);
  useKey("ArrowUp", focusNextElement("up"), {}, []);
  useKey("ArrowDown", focusNextElement("down"), {}, []);

  //Selecting first tab that has unread notifications
  useEffect(() => {
    if (newNotificationTotalCount > 0) {
      const tabWithUnread = _.find(categoryTabs, tab => tab?.unreadCount > 0);
      if (tabWithUnread && tabWithUnread?.value !== tabValue) {
        onToggleActiveTab(tabWithUnread?.value);
      }
    }
  }, []);

  return (
    <div className={containerClasses} ref={notificationCenterRef}>
      <div className={classes.topBarContainer}>
        <div className={classes.headerContainer}>
          <div className={classes.notificationLabelContainer}>
            <div className={classes.headerText}>
              {t("announcement:notifications")}
              {getNewValueWhenGlobalHeaderEnabled(
                null,
                newNotificationTotalCount ? (
                  <span className={classes.headerTextCount}>
                    ({newNotificationTotalCount})
                  </span>
                ) : null
              )}
            </div>
            <div className={classes.unreadAndMenu}>
              <Tooltip
                placement="top"
                tooltip={t("announcement:mark_all_as_read")}
              >
                <IconButton
                  variant="plain"
                  icon={<MarkAsReadOutlined />}
                  onClick={onMarkAllReadClicked}
                  className="allRead"
                  ref={node => (menuRef.current[0] = node)}
                  testId={`${testId}-markAllAsRead`}
                />
              </Tooltip>
              <Tooltip placement="top" tooltip={t("common:show_only_unread")}>
                <IconButton
                  variant="plain"
                  isActivated={showOnlyUnread}
                  icon={
                    showOnlyUnread ? (
                      <FilterThreeLinesFilled />
                    ) : (
                      <FilterThreeLinesOutlined />
                    )
                  }
                  testId={`${testId}-showOnlyUnread`}
                  ref={node => (menuRef.current[1] = node)}
                  onClick={handleTickButtonClick}
                />
              </Tooltip>
              <div className={classes.separatorLine}></div>
              <Tooltip placement="top" tooltip={t("common:settings")}>
                <IconButton
                  variant="plain"
                  icon={<SettingsOutlined />}
                  onClick={goToNotificationSettings}
                  ref={node => (menuRef.current[2] = node)}
                  testId={`${testId}-settings`}
                />
              </Tooltip>
              {getNewValueWhenGlobalHeaderEnabled(
                null,
                <Tooltip placement="top" tooltip={t("common:close")}>
                  <IconButton
                    variant="plain"
                    icon={<CloseOutlined />}
                    onClick={handleCloseSidebar}
                    ref={node => (menuRef.current[3] = node)}
                    testId={`${testId}-close`}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <div className={classes.tabContainer}>
          <Tabs
            options={categoryTabs}
            value={tabValue}
            onChange={onToggleActiveTab}
            type="flexible"
            testId={`${testId}-notificationCategoryTabs`}
            dsVersion={getNewValueWhenGlobalHeaderEnabled("1.0", "2.0")}
          />
        </div>
      </div>
      {isActiveTabAnnouncement && !isClassAnnouncementEnabled ? (
        <AnnouncementButtons
          onViewAllAnnouncementClick={onViewAllAnnouncementClick}
          t={t}
          testId={testId}
        />
      ) : null}
      {showNotificationCenter ? (
        <NotificationList
          closeNotificationCenter={closeNotificationCenter}
          notificationListRef={notificationListRef}
          activeTabFilterValue={tabFilterValue}
          courses={courses}
          isFromSideBar={isFromSideBar}
          showOnlyUnread={showOnlyUnread}
          toggleShowNotificationCenter={toggleShowNotificationCenter}
          testId={testId}
        />
      ) : null}
    </div>
  );
});

NotificationCenterV2.displayName = "NotificationCenterV2";

const mapActionCreators = {
  goToPlatformHomeChildRoute,
  goToSnpHomeChildRoute,
  allNotificationsRead,
  setHomePageSidebar,
  setThirdPaneNotificationCenterActiveTab,
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const userId = getUserId(state);
  const userType = getUserType(state);
  const userEntityType = getUserEntityType(state);
  const academicYearSelected = getSelectedAcademicYear(state);
  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const thirdPaneNotificationCenterActiveTab = _.get(
    state,
    "platform.thirdPaneNotificationCenterActiveTab"
  );
  const isCurrentAcademicYear =
    academicYearSelected.isCurrentAcademicYear &&
    !getIsAcademicYearEnded(academicYearSelected);
  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);
  const isAdminPortal = state.login.activeTab === "admin";
  const isStaff = getIsStaff(state);
  const notificationCategoryTabs = getNotificationCategoryTabs({
    userId,
    userType,
    type: userEntityType,
    isCurriculumProgramFree,
    t: _.get(ownProps, "t"),
    getTabLabelComponent: getNewValueWhenGlobalHeaderEnabled(
      getTabLabelComponent,
      args =>
        getTabLabelComponentV2({
          ...args,
          thirdPaneNotificationCenterActiveTab,
        })
    ),
    currentCurriculumProgramType,
    isAdminPortal,
  });

  const newNotificationTotalCount = getNewNotificationCount({
    userId,
    userType,
    type: userEntityType,
    isCurriculumProgramFree,
  });
  const academicYearId = _.get(academicYearSelected, "id");
  const curriculumProgramIds = _.compact(
    getSelectedCurriculumProgramIds(state)
  );
  const organizationCurriculumPrograms = _.get(
    state,
    "platform.organizationCurriculumPrograms"
  );
  const userTags = userInfo?.tags || [];
  const courseFilters = getCourseFiltersMemoize({
    curriculumProgramIds,
    organizationCurriculumPrograms,
    isCurrentAcademicYear,
    academicYear: academicYearId,
    userTags,
    user_type: userType,
  });
  const {
    userEntityType: formattedUserEntityType,
  } = getUserIdAndEntityTypeByUserMemoize({
    userInfo,
  });
  const portalType = "PLANNER";
  const formattedUserId =
    userType === "parent"
      ? userInfo.childID
      : getUserIdOnPortalType({ state, portalType });

  return {
    userType,
    categoryTabs: ownProps.categoryTabs
      ? ownProps.categoryTabs
      : notificationCategoryTabs,
    isClassAnnouncementEnabled: getIsClassAnnouncementEnabled(),
    isStaff,
    newNotificationTotalCount: newNotificationTotalCount,
    academicYearId,
    courseFilters,
    formattedUserId,
    formattedUserEntityType,
  };
};

const getCoursesByIsDemo = ({ courses, isDemo }) => {
  return _.filter(courses, course => course.isDemo === isDemo);
};
const getCoursesMemoizeCacheGen = param =>
  JSON.stringify({
    courseIds: _.map(param?.courses, "id"),
    isDemo: param?.isDemo,
  });
const getCoursesByIsDemoMemoize = _.memoize(
  getCoursesByIsDemo,
  getCoursesMemoizeCacheGen
);

NotificationCenterV2.propTypes = {};

export default compose(
  I18nHOC({ resource: ["common", "notifications", "announcement"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserCoursesQuery, {
    name: "getUserCourses",
    skip: ({ formattedUserId, formattedUserEntityType }) =>
      !formattedUserId || !formattedUserEntityType,
    options: ({
      formattedUserId,
      formattedUserEntityType,
      courseFilters,
      isCurriculumProgramFree,
      academicYearId,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: formattedUserId,
        type: formattedUserEntityType,
        filters: courseFilters,
        courseGradeFilters: { academicYearId },
        isCurriculumProgramFree,
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      ownProps: {
        formattedUserId,
        formattedUserEntityType,
        courseFilters,
        academicYearId,
        isCurriculumProgramFree,
      },
    }) {
      //console.log("courseFilters", courseFilters);
      const queryData = getUserCoursesFromCache({
        id: formattedUserId,
        type: formattedUserEntityType,
        filters: courseFilters,
        courseGradeFilters: { academicYearId },
        isCurriculumProgramFree,
      });
      const allCourses = _.get(queryData, "courses", []);
      const courses = getCoursesByIsDemoMemoize({
        courses: allCourses,
        isDemo: false,
      });
      return {
        courses,
      };
    },
  })
)(NotificationCenterV2);
